import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from "../css/ResetPassword.module.css"
import validator from 'validator';
import { IoEyeOffOutline, IoEyeOutline, IoMailOutline, IoWarning } from "react-icons/io5";
import { TfiTimer } from "react-icons/tfi";
import LoadingButton from '../../Loaders/js/LoadingButton';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { forgot_password, reset_password, verify_code } from '../../../actions/authActions';
import { AUTH_FAIL, AUTH_RESET, SET_AUTH_LOADING } from '../../../actions/authTypes';
import AuthScreen from './AuthScreen'
import { RiErrorWarningFill, RiShieldKeyholeLine } from "react-icons/ri";
import { TbMailOpened } from "react-icons/tb";
import { GrUnlock } from "react-icons/gr";
import MobileHeader from './MobileHeader';
import LogoItem from '../../Logo/LogoItem';
import { ResetFloatingCards } from './FloatingCards';
import useTimer from '../../../hooks/useTimer';
import { IoMdCloseCircle } from 'react-icons/io';
import { CgPassword } from 'react-icons/cg';

const ResetPassword = () => {
    const [data,setData] = useState({email:"",code:["","","","",""],new_password:"",confirm_password:""})
    const [codeSent,setCodeSent] = useState(false)
    const [showPassword,setShowPassword] = useState(false)
    const [expired,setExpired] = useState(false)
    const codeRefs = useRef([])
    const loading = useSelector((state)=>state.authState.loading)
    const [codeValidated,setCodeValidated] = useState(false)
    const [errors,setErrors] = useState({})
    const crashError = useSelector((state) => state.authState.authError)

    const dispatch = useDispatch()
    const navigate = useNavigate()

    useEffect(()=>{
      return () => {
        dispatch({type: AUTH_RESET})
      }
    },[dispatch])

    const step = useMemo(()=>{
      const num = !codeSent ? 1 : (!codeValidated ? 2 : 3)
      const text = num === 1 ? "Step 1 of 3 - Enter Email" : (num === 2 ? "Step 2 of 3 - Verify Code" : "Step 3 of 3 - Set New Password")
      return {num,text}  
    },[codeSent,codeValidated])

    const sendResetLink = async() => {
      setCodeSent(false)
      setData({...data,code:["","","","",""]})
      setExpired(false)
      try{
        await dispatch(forgot_password(data.email))
        setCodeSent(true)
      }
      catch(error){
        let apiErrors = error.response?.data ?? {}
        setErrors({...errors,...apiErrors})
      }
    }

    const reset = (withError = false) => {
      setCodeSent(false)
      setCodeValidated(false)
      if (!withError) setData({email:"",code:["","","","",""],new_password:"",confirm_password:""})
      else setData({...data,code:["","","","",""],new_password:"",confirm_password:""})

    }

    const resetPassword = async() => {
      let valid = true
      if (data.new_password.length < 8){
        valid = false
        setErrors((prevErrors)=> {return {...prevErrors,new_password:"At least 8 characters"}})
      }
      if (data.new_password !== data.confirm_password){
        valid = false
        setErrors((prevErrors)=> {return {...prevErrors,confirm_password:"Passwords do not match"}})
      }
      if (valid){
        try{
          await dispatch(reset_password(data.email,data.new_password))
          navigate("/login",{state: {successMessage: "Password Changed Successfully"}})
        }
        catch(err){
          let apiErrors = err.response?.data ?? {}
          setErrors({...errors,...apiErrors})
          if (err.response?.status === 400 && 'email' in apiErrors){
            reset(true)
          }
        }
      }
    }

    const verifyCode = async() => {
        try{
          await dispatch(verify_code(data.email,data.code.join("")))
          setCodeValidated(true)
        }
        catch(err){
          if (err.response?.status === 410){
            setExpired(true)
            setCodeSent(false)
            setData({...data,code:["","","","",""]})
          }
          else if (err.response?.status === 400){
            setErrors((prevErrors)=> {return {...prevErrors,code:"Incorrect code"}})
          } 
        }
    }

    const handleSubmit = async(e) => {
      e.preventDefault()
      document.activeElement.blur()
      if (step.num === 1){
        if (!validator.isEmail(data.email)){
          setErrors((prevErrors)=> {return {...prevErrors,email:"Invalid email address"}})
        }
        else{
          await sendResetLink()
        }
      }
      else if (step.num === 2){
        await verifyCode()
      }
      else{
        await resetPassword()
      }
    }

    const handleChange = (e) => {
      let name = e.target.name
      let value = e.target.value
      setData({...data,[name]:value})
      if (name === "email" && errors.email){
        if (errors.email === "Invalid email address"){
          if (validator.isEmail(value) || !value) setErrors((prevErrors)=> { return {...prevErrors,email:""}})
        }
        else{
          setErrors((prevErrors)=> { return {...prevErrors,email:""}})
        }
      }
      else if (name === "new_password"){
        if (value.length >= 8 && errors.new_password) setErrors((prevErrors)=>{return {...prevErrors,new_password:""}})
        if (errors.confirm_password && data.confirm_password === value) setErrors((prevErrors)=>{return {...prevErrors,confirm_password:""}})
      }
      else if (name === "confirm_password" && errors.confirm_password && (value === data.new_password || !value)){
        setErrors({...errors,confirm_password:""})
      }
    }

    const handleCodeChange = (e,index) => {
      let value = e.target.value
      if (!(/^-?\d+$/.test(value)) && value.length){
        return
      }
      setData((prevData)=>{
        let oldCode = [...prevData.code]
        oldCode[index] = value
        return {...prevData,code:oldCode}
      })
      if (index < 4 && value.length){
        codeRefs.current[index+1].focus()
      }
      if (errors.code){
        setErrors((prevErrors)=>{
          let copy = {...prevErrors}
          delete copy.code
          return copy
        })
      }
    }

    const handleKeyDown = (e,index) => {
      if (e.keyCode === 37 && index > 0){
        codeRefs.current[index-1].focus()
      }
      else if (e.keyCode === 39 && index < 4){
        codeRefs.current[index+1].focus()
      }
      else if ((e.keyCode === 46 || e.keyCode === 8) && !e.target.value.length && index > 0){
        e.preventDefault()
        codeRefs.current[index-1].focus()
      }
    }

    const handlePaste = (e,index) => {
      e.preventDefault()
      let clipboardData = e.clipboardData || window.clipboardData
      let pastedData = clipboardData.getData('Text')
      let length = pastedData.length < 5 - index ? pastedData.length : 5 - index
      let new_code = pastedData.substring(0,length)
      if (/^-?\d+$/.test(new_code)){
        for (let i = 0; i < length; i ++){
          setData((prevData)=>{
            let oldCode = [...prevData.code]
            oldCode[index+i] = new_code[i]
            return {...prevData,code:oldCode}
          })
        }
      }
      if (length + index === 5){
        codeRefs.current[4].focus()
      }
      else{
        codeRefs.current[index+length].focus()
      }
    }

    const setCodeRefs = useCallback((el,index)=>{
      if (el){
        if (!codeRefs.current[index] && index === 0){
          el.focus()
        }
        codeRefs.current[index] = el
      }
    },[])

    const isDisabled = useMemo(() => {
      if (step.num === 1){
        return !data.email.trim() || errors.email
      }
      else if (!codeValidated){
        return data.code.some(i => !i) || errors.code
      }
      else{
        return errors.new_password || errors.confirm_password || !data.new_password || !data.confirm_password || expired
      }
    },[errors,data,step,codeValidated,expired])

    const restart = () => {
      reset()
      setErrors({})
      setShowPassword(false)
      dispatch({type: SET_AUTH_LOADING, payload: false})
      dispatch({type: AUTH_FAIL, payload: false})
    }

    const steps = [
      {title: "Forgot Password?",subtext: "No worries, enter your email and we'll send you a reset code",icon:<RiShieldKeyholeLine/>},
      {title: "Verify Password",subtext: <p>We sent a 5-digit code to <b>{data.email}</b>. Enter it below to verify your identity and set a new password.</p>,icon:<TbMailOpened/>},
      {title: "Set new Password",subtext: "Must be at least 8 characters",icon:<GrUnlock/>},
    ]

    const clearError = () => {
      if (expired) setExpired(false)
      if (crashError) dispatch({type: "AUTH_CLEAR_ERROR"})
    }

  return (
    <AuthScreen
      form = {
        <div className = {styles.box}>
          <MobileHeader title = "Reset Password" subtitle = {step.text} />
          <div className = {styles.topNav}>
            <div className = {styles.appRep}><LogoItem/></div>
            <p className = {styles.navLink}>Rememberd it? <NavLink to = "/login">Sign in</NavLink></p>
          </div>
          <main>
            <div className = {styles.titleBox}>
            <div className = {styles.steps}>
              <div className = {`${styles.step} ${step.num === 1 ? styles.active : ""} ${step.num > 1 ? styles.done : ""}`}></div>
              <div className = {`${styles.step} ${step.num === 2 ? styles.active : ""}  ${step.num > 2 ? styles.done : ""}`}></div>
              <div className = {`${styles.step} ${step.num === 3 ? styles.active : ""}`}></div>
            </div>
              <div className = {styles.icon}>{steps[step.num-1].icon}</div>
              <div className = {styles.title}>{steps[step.num-1].title}</div>
              <div className = {styles.instructions}>{steps[step.num-1].subtext}</div>
            </div>
            <form className = {styles[`step${step.num}`]} onSubmit = {handleSubmit}>
              {(crashError || expired) && <div data-testid = {crashError ? "crash-error" : "expiry-box"} className = {`${styles.error} ${expired ? styles.warning : ""}`}>
                <div className = {styles.errorInfo}>
                  <div className = {styles.warningIcon}><IoWarning/></div>
                  {crashError ? "Something unexpected occured" : "Reset Session expired."}
                </div>
                <div className = {styles.closeIcon} onClick = {()=>clearError()}><IoMdCloseCircle/></div>
              </div>}
              {step.num === 1 && <div className = {styles.content}>
                <div className = {styles.inputWrapper}>
                  <div className= {`${styles.inputBox} ${errors.email ? styles.errorBox : ""}`}>
                    {errors.email ? <div className = {styles.errorIcon}><RiErrorWarningFill/></div> : <div className = {styles.inputIcon}><IoMailOutline/></div>}
                    <div className = {styles.inputContainer}>
                      <input
                        type = "text"
                        value = {data.email}
                        name = "email"
                        onChange = {handleChange}
                        placeholder = "Enter your email"
                        autoComplete = "off"
                      />
                    </div>
                  </div>
                  {errors.email && <div data-testid = "email-error" className = {styles.inputError}>{errors.email}</div>}
                </div>
              </div>}
              {step.num === 2 &&
                <div data-testid = "code-box" className = {styles.content}>
                  <div className = {styles.inputWrapper}>
                    <ExpiryTime
                      setExpired = {()=>{
                        setExpired(true)
                        restart()
                      }}
                    />
                    <div className={`${styles.codeBoxes} ${errors.code ? styles.codeError : ""}`}>
                      {data.code.map((item,index)=>(
                        <input
                          ref = {(el)=>setCodeRefs(el,index)}
                          type = "text"
                          onChange = {(e)=>handleCodeChange(e,index)}
                          onKeyDown = {(e)=>handleKeyDown(e,index)}
                          onPaste = {(e)=>handlePaste(e,index)}
                          key = {index}
                          data-testid = {`code-${index}`}
                          value = {item}
                          autoComplete = "off"
                          maxLength = {1}
                          disabled = {expired}
                        />
                      ))}
                    </div>
                    {errors.code && <div data-testid = "code-error" className = {styles.inputError}>{errors.code}</div>}
                  </div>
                  <ResendComponent sendResetLink = {()=>sendResetLink()}/>
                </div>
              }
              {step.num === 3 && <div data-testid = "reset-box" className = {styles.content}>
                <div className = {styles.inputWrapper}>
                  <label 
                    htmlFor = "new_password"
                  >
                    Password
                  </label>
                  <div className={`${styles.inputBox} ${errors.new_password ? styles.errorBox : ""}`}>
                  {errors.new_password ? <div className = {styles.errorIcon}><RiErrorWarningFill/></div> : <div className = {styles.inputIcon}><CgPassword/></div>}
                    <div className = {styles.inputContainer}>
                      <input
                        type = {showPassword ? "text" : "password"}
                        value = {data.new_password}
                        name = "new_password"
                        onChange = {handleChange}
                        placeholder = "Enter your password"
                        autoComplete = "off"
                      />
                    </div>
                    <div className = {styles.visibilityIcon} onClick = {()=>setShowPassword(!showPassword)}>{showPassword ? <IoEyeOffOutline/> : <IoEyeOutline/>}</div>
                  </div>
                  {errors.new_password && <div data-testid = "password-error" className = {styles.inputError}>{errors.new_password}</div>}
                </div>
                <div className = {styles.inputWrapper}>
                  <label 
                    htmlFor = "confirm_password"
                  >
                    Confirm Password
                  </label>
                  <div className={`${styles.inputBox} ${errors.confirm_password ? styles.errorBox : ""}`}>
                  {errors.confirm_password ? <div className = {styles.errorIcon}><RiErrorWarningFill/></div> : <div className = {styles.inputIcon}><CgPassword/></div>}
                    <div className = {styles.inputContainer}>
                      <input
                        type = "password"
                        value = {data.confirm_password}
                        name = "confirm_password"
                        onChange = {handleChange}
                        placeholder = "Re-type password"
                        autoComplete = "off"
                      />
                    </div>
                  </div>
                  {errors.confirm_password && <div data-testid = "confirm-password-error" className = {styles.inputError}>{errors.confirm_password}</div>}
                </div>
              </div>}
              <button type = "submit" className = {loading ? styles.loading : ""} disabled = {isDisabled}>
                {step.num === 1 || step.num === 3 ? "Reset Password" : "Continue"}
                <div className = {styles.arrowCircle}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </div>
                {loading && <LoadingButton/>}
              </button>
              {step.num === 3 && 
                <ExpiryTime
                  setExpired = {()=>{
                    setExpired(true)
                    restart()
                  }}
                />
              }
            </form>
          </main>
        </div>
        }
        floatingContent={<ResetFloatingCards/>}
      />
  )
}

const ResendComponent = ({sendResetLink}) => {
  const { formatted: time, isExpired: canResend } = useTimer(60)

  return(
    <div className = {styles.resend}>Didn't get a code yet? <span className = {!canResend ? styles.disabled : ""} onClick = {()=>sendResetLink()}>{`Resend ${!canResend ? `in ${time}` : ""}`}</span></div>
  )
}

const ExpiryTime = ({setExpired}) => {
  const { formatted: time, isExpired = false } = useTimer(300)

  useEffect(()=>{
    if (isExpired){
      setExpired()
    }
  },[isExpired,setExpired])

  return(
    <div className = {styles.expiryTime}>
      <div className = {styles.timerIcon}><TfiTimer/></div> 
      <div className = {styles.time}>{time} remaining</div>
    </div>
  )
}

export default ResetPassword